import { AbstractConnector } from "@web3-react/abstract-connector";
import { AbstractConnectorArguments, ConnectorUpdate } from "@web3-react/types";
import warning from "tiny-warning";
import { Send, SendOld, SendReturn, SendReturnResult } from "./types";

const __DEV__ = true;

function parseSendReturn(sendReturn: SendReturnResult | SendReturn): any {
  return Object.prototype.hasOwnProperty.call(sendReturn, "result")
    ? sendReturn.result
    : sendReturn;
}

export class NoMonstaProviderError extends Error {
  public constructor() {
    super();
    this.name = this.constructor.name;
    this.message = "No monsta provider was found on window.monsta.";
  }
}

export class UserRejectedRequestError extends Error {
  public constructor() {
    super();
    this.name = this.constructor.name;
    this.message = "The user rejected the request.";
  }
}

export class MonstaConnector extends AbstractConnector {
  constructor(kwargs: AbstractConnectorArguments) {
    super(kwargs);

    this.handleNetworkChanged = this.handleNetworkChanged.bind(this);
    this.handleChainChanged = this.handleChainChanged.bind(this);
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  private handleChainChanged(chainId: string | number): void {
    if (__DEV__) {
      console.log("Handling 'chainChanged' event with payload", chainId);
    }
    this.emitUpdate({ chainId, provider: window.monsta });
  }

  private handleAccountsChanged(accounts: string[]): void {
    if (__DEV__) {
      console.log("Handling 'accountsChanged' event with payload", accounts);
    }
    if (accounts.length === 0) {
      this.emitDeactivate();
    } else {
      this.emitUpdate({ account: accounts[0] });
    }
  }

  private handleClose(code: number, reason: string): void {
    if (__DEV__) {
      console.log("Handling 'close' event with payload", code, reason);
    }
    this.emitDeactivate();
  }

  private handleNetworkChanged(networkId: string | number): void {
    if (__DEV__) {
      console.log("Handling 'networkChanged' event with payload", networkId);
    }
    this.emitUpdate({ chainId: networkId, provider: window.monsta });
  }

  public async activate(): Promise<ConnectorUpdate> {
    if (!window.monsta) {
      throw new NoMonstaProviderError();
    }

    if (window.monsta.on) {
      window.monsta.on("chainChanged", this.handleChainChanged);
      window.monsta.on("accountsChanged", this.handleAccountsChanged);
      window.monsta.on("close", this.handleClose);
      window.monsta.on("networkChanged", this.handleNetworkChanged);
    }

    if (!(window.monsta as any).isMetaMask) {
      (window.monsta as any).autoRefreshOnNetworkChange = false;
    }

    // try to activate + get account via eth_requestAccounts
    let account;
    try {
      account = await (window.monsta.send as Send)("eth_requestAccounts").then(
        (sendReturn) => parseSendReturn(sendReturn)[0]
      );
    } catch (error) {
      if ((error as any).code === 4001) {
        throw new UserRejectedRequestError();
      }
      warning(
        false,
        "eth_requestAccounts was unsuccessful, falling back to enable"
      );
    }

    // if unsuccessful, try enable
    if (!account) {
      // if enable is successful but doesn't return accounts, fall back to getAccount (not happy i have to do this...)
      account = await window.monsta
        .enable()
        .then((sendReturn) => sendReturn && parseSendReturn(sendReturn)[0]);
    }

    return { provider: window.monsta, ...(account ? { account } : {}) };
  }

  public async getProvider(): Promise<any> {
    return window.monsta;
  }

  public async getChainId(): Promise<number | string> {
    if (!window.monsta) {
      throw new NoMonstaProviderError();
    }

    let chainId;
    try {
      chainId = await (window.monsta.send as Send)("eth_chainId").then(
        parseSendReturn
      );
    } catch {
      warning(
        false,
        "eth_chainId was unsuccessful, falling back to net_version"
      );
    }

    if (!chainId) {
      try {
        chainId = await (window.monsta.send as Send)("net_version").then(
          parseSendReturn
        );
      } catch {
        warning(
          false,
          "net_version was unsuccessful, falling back to net version v2"
        );
      }
    }

    if (!chainId) {
      try {
        chainId = parseSendReturn(
          (window.monsta.send as SendOld)({ method: "net_version" })
        );
      } catch {
        warning(
          false,
          "net_version v2 was unsuccessful, falling back to manual matches and static properties"
        );
      }
    }

    if (!chainId) {
      if ((window.monsta as any).isDapper) {
        chainId = parseSendReturn(
          (window.monsta as any).cachedResults.net_version
        );
      } else {
        chainId =
          (window.monsta as any).chainId ||
          (window.monsta as any).netVersion ||
          (window.monsta as any).networkVersion ||
          (window.monsta as any)._chainId;
      }
    }
    return chainId;
  }

  public async getAccount(): Promise<null | string> {
    if (!window.monsta) {
      throw new NoMonstaProviderError();
    }

    let account;
    try {
      account = await (window.monsta.send as Send)("eth_accounts").then(
        (sendReturn) => parseSendReturn(sendReturn)[0]
      );
    } catch {
      warning(false, "eth_accounts was unsuccessful, falling back to enable");
    }

    if (!account) {
      try {
        account = await window.monsta
          .enable()
          .then((sendReturn) => parseSendReturn(sendReturn)[0]);
      } catch {
        warning(
          false,
          "enable was unsuccessful, falling back to eth_accounts v2"
        );
      }
    }

    if (!account) {
      account = parseSendReturn(
        (window.monsta.send as SendOld)({ method: "eth_accounts" })
      )[0];
    }

    return account;
  }

  public deactivate() {
    if (window.monsta && window.monsta.removeListener) {
      window.monsta.removeListener("chainChanged", this.handleChainChanged);
      window.monsta.removeListener(
        "accountsChanged",
        this.handleAccountsChanged
      );
      window.monsta.removeListener("close", this.handleClose);
      window.monsta.removeListener("networkChanged", this.handleNetworkChanged);
    }
  }

  public async isAuthorized(): Promise<boolean> {
    if (!window.monsta) {
      return false;
    }

    try {
      return await (window.monsta.send as Send)("eth_accounts").then(
        (sendReturn) => {
          if (parseSendReturn(sendReturn).length > 0) {
            return true;
          } else {
            return false;
          }
        }
      );
    } catch {
      return false;
    }
  }
}
